import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createQueryKey } from 'src/utils/query/createQueryKey';
import { PublicKey } from '@solana/web3.js';

interface PrivyWalletMappingData {
	embeddedWallet: string;
	authority: string;
	insertedTime: string;
	updatedTime: string;
}

interface PrivyWalletMappingResponse {
	success: boolean;
	data: PrivyWalletMappingData | null;
}

interface UpsertPrivyWalletMappingParams {
	authority: string;
	embeddedWallet: string;
}

interface UpsertPrivyWalletMappingResponse {
	success: boolean;
	message: string;
	data: PrivyWalletMappingData;
}

/**
 * Hook to fetch the Privy embedded wallet mapping for a given authority.
 */
export const usePrivyWalletMapping = (
	authority: PublicKey | string | null | undefined
) => {
	const authorityStr = authority?.toString();

	const queryFn = async (): Promise<PrivyWalletMappingData | null> => {
		if (!authorityStr) {
			return null;
		}

		const response = await fetch(
			`/api/privy-wallet-mappings?authority=${authorityStr}`
		);

		if (!response.ok) {
			throw new Error(
				`Failed to fetch Privy wallet mapping: ${response.status}`
			);
		}

		const json: PrivyWalletMappingResponse = await response.json();

		return json.data;
	};

	return useQuery({
		queryKey: createQueryKey('privyWalletMapping', authorityStr),
		queryFn,
		enabled: !!authorityStr,
		staleTime: 60 * 60 * 1000, // Cache for 1 hour
		refetchOnWindowFocus: false,
	});
};

/**
 * Hook to upsert (create or update) a Privy wallet mapping.
 */
export const useUpsertPrivyWalletMapping = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (
			params: UpsertPrivyWalletMappingParams
		): Promise<PrivyWalletMappingData> => {
			const response = await fetch('/api/privy-wallet-mappings', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(params),
			});

			if (!response.ok) {
				const errorData = await response.json();
				throw new Error(
					errorData.message || 'Failed to upsert Privy wallet mapping'
				);
			}

			const json: UpsertPrivyWalletMappingResponse = await response.json();
			return json.data;
		},
		onSuccess: (data) => {
			// Invalidate and refetch the query for this authority
			queryClient.invalidateQueries({
				queryKey: createQueryKey('privyWalletMapping', data.authority),
			});
		},
	});
};
